'use client';

import { createContext, useContext, useState, type PropsWithChildren } from 'react';

const CspNonceContext = createContext<string | null>(null);

export interface CspNonceProviderProps {
	nonce: string | null;
}

/**
 * Pins the CSP nonce for the lifetime of the current document. The browser only
 * ever enforces the `Content-Security-Policy(-Report-Only)` header from the
 * original top-level navigation response — it never re-adopts a nonce from a
 * later RSC-payload fetch triggered by a client-side (soft) navigation. Mounted
 * once in the root layout, which — unlike `PageServerProvider` (invoked fresh
 * inside every `page.tsx`, and therefore re-run on every navigation) — never
 * remounts on a soft nav; `useState`'s initial value here is fixed to whatever
 * nonce this document's ORIGINAL request carried, and stays that way no matter
 * how many client-side transitions happen afterward.
 */
export const CspNonceProvider = ({ nonce, children }: PropsWithChildren<CspNonceProviderProps>) => {
	const [pinnedNonce] = useState(nonce);
	return <CspNonceContext.Provider value={pinnedNonce}>{children}</CspNonceContext.Provider>;
};

export const useCspNonce = (): string | null => useContext(CspNonceContext);
